
import React, { useState, useMemo, useEffect } from 'react';
import { WealthItem, Transaction } from '../types';
import { formatCurrency, maskCurrency, parseCurrencyToNumber } from '../utils/helpers';
import ConfirmDialog from './ConfirmDialog';

interface WealthProps {
  items: WealthItem[];
  onSave: (item: WealthItem) => void;
  onDelete: (id: string) => void;
  onTriggerVerify: (title: string, message: string, callback: () => void, icon?: string) => void;
  transactions: Transaction[];
  onAddTransaction: (t: Transaction) => void;
  needsSetup: boolean;
  onTriggerSetup: () => void;
}

const Wealth: React.FC<WealthProps> = ({ items, onSave, onDelete, onTriggerVerify, transactions, onAddTransaction, needsSetup, onTriggerSetup }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WealthItem | null>(null);
  const [selectedItemForDeposit, setSelectedItemForDeposit] = useState<WealthItem | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; id: string | null }>({
    isOpen: false,
    id: null
  });

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('0,00');
  const [target, setTarget] = useState('0,00');
  const [type, setType] = useState<'savings' | 'emergency'>('savings');

  const [depositAmount, setDepositAmount] = useState('0,00');

  const [showValues, setShowValues] = useState(() => {
    const saved = localStorage.getItem('equilibra_privacy_mode');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    const handleSync = () => {
      const saved = localStorage.getItem('equilibra_privacy_mode');
      setShowValues(saved === null ? true : saved === 'true');
    };
    window.addEventListener('privacyModeChanged', handleSync);
    return () => window.removeEventListener('privacyModeChanged', handleSync);
  }, []);

  const togglePrivacy = () => {
    const newVal = !showValues;
    setShowValues(newVal);
    localStorage.setItem('equilibra_privacy_mode', newVal.toString());
    window.dispatchEvent(new Event('privacyModeChanged'));
  };

  const maskValue = (val: number) => {
    if (!showValues) return "••••••";
    return formatCurrency(val);
  };

  const stats = useMemo(() => {
    const totalSavings = items.filter(i => i.type === 'savings').reduce((acc, i) => acc + i.amount, 0);
    const totalEmergency = items.filter(i => i.type === 'emergency').reduce((acc, i) => acc + i.amount, 0);
    return { 
      totalSavings, 
      totalEmergency, 
      totalWealth: totalSavings + totalEmergency 
    };
  }, [items]);

  const handleOpenModal = (item?: WealthItem) => {
    if (needsSetup) {
      onTriggerSetup();
      return;
    }
    
    if (item) {
      onTriggerVerify(
        'Autorizar Alteração',
        `Para modificar os dados de ${item.name}, insira sua Chave Mestra.`,
        () => {
          setEditingItem(item);
          setName(item.name);
          setAmount(maskCurrency(item.amount));
          setTarget(item.targetAmount ? maskCurrency(item.targetAmount) : '0,00');
          setType(item.type);
          setIsModalOpen(true);
        },
        'fa-vault'
      );
    } else {
      setEditingItem(null);
      setName('');
      setAmount('0,00');
      setTarget('0,00');
      setType('savings');
      setIsModalOpen(true);
    }
  };

  const handleOpenDeposit = (item: WealthItem) => {
    if (needsSetup) {
      onTriggerSetup();
      return;
    }
    setSelectedItemForDeposit(item);
    setDepositAmount('0,00');
    setIsDepositModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numericAmount = parseCurrencyToNumber(amount);
    const numericTarget = parseCurrencyToNumber(target);
    
    onSave({
      id: editingItem?.id || Math.random().toString(36).substr(2, 9),
      name,
      amount: numericAmount,
      targetAmount: numericTarget > 0 ? numericTarget : undefined,
      type
    });
    setIsModalOpen(false);
  };

  const handleDepositSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const numericDeposit = parseCurrencyToNumber(depositAmount);
    if (!selectedItemForDeposit || numericDeposit <= 0) return;

    onSave({
      ...selectedItemForDeposit,
      amount: selectedItemForDeposit.amount + numericDeposit
    });

    onAddTransaction({
      id: Math.random().toString(36).substr(2, 9),
      description: `Aporte: ${selectedItemForDeposit.name}`,
      amount: numericDeposit,
      type: 'expense',
      category: 'Investimento',
      date: new Date().toISOString().split('T')[0],
      wealthItemId: selectedItemForDeposit.id
    });

    setIsDepositModalOpen(false);
  };

  return (
    <div className="animate-in fade-in duration-700 pb-20 max-w-4xl mx-auto px-1">
      <div className="sticky top-0 z-40 bg-slate-100/80 dark:bg-slate-950/80 backdrop-blur-xl pt-[max(0.75rem,env(safe-area-inset-top))] pb-4 space-y-3 -mx-4 px-4 border-b border-slate-200/50 dark:border-slate-800/50 md:relative md:bg-transparent md:border-none md:p-0 md:mb-8 transition-all">
        <header className="flex justify-between items-center">
          <div>
            <h1 className="text-xl md:text-2xl font-black text-indigo-950 dark:text-slate-50 heading-display uppercase tracking-tight leading-none">Patrimônio</h1>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.4)]"></div>
              <p className="text-slate-500 dark:text-slate-400 text-[9px] md:text-[10px] uppercase font-black tracking-[0.2em]">Equilibra Asset Management • Soberania de Dados</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={togglePrivacy}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 w-9 h-9 md:w-12 md:h-12 rounded-xl flex items-center justify-center shadow-premium active:scale-95 transition-all hover:border-blue-500/50 group"
              title={showValues ? "Ocultar Valores" : "Mostrar Valores"}
            >
              <i className={`fa-solid ${showValues ? 'fa-eye' : 'fa-eye-slash'} text-xs md:text-lg transition-transform group-hover:scale-110`}></i>
            </button>
            <button 
              onClick={() => handleOpenModal()}
              className="bg-blue-600 hover:bg-blue-700 text-white w-9 h-9 md:w-12 md:h-12 rounded-xl flex items-center justify-center shadow-2xl shadow-blue-500/30 active:scale-95 transition-all group"
            >
              <i className="fa-solid fa-plus text-sm md:text-xl transition-transform group-hover:rotate-90"></i>
            </button>
          </div>
        </header>
      </div>

      <div className="space-y-6">
        <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 p-5 md:p-6 rounded-2xl shadow-premium relative overflow-hidden transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 group">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.05),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.03] via-transparent to-emerald-500/[0.03] opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
          
          <div className="space-y-0.5 relative z-10">
            <p className="text-slate-400 dark:text-slate-500 text-[9px] font-black uppercase tracking-[0.2em]">Patrimônio Líquido Consolidado</p>
            <h2 className="text-2xl md:text-4xl font-black text-indigo-950 dark:text-white tracking-tighter leading-none heading-display">{maskValue(stats.totalWealth)}</h2>
          </div>
          
          <div className="flex items-center gap-6 md:gap-10 md:pl-10 md:border-l border-slate-100 dark:border-slate-800/50 relative z-10">
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.4)]"></div>
                <p className="text-slate-400 dark:text-slate-500 text-[8px] font-black uppercase tracking-[0.15em]">Poupança</p>
              </div>
              <p className="text-lg md:text-xl font-black text-indigo-900 dark:text-blue-400 heading-display">{maskValue(stats.totalSavings)}</p>
            </div>
            
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)]"></div>
                <p className="text-slate-400 dark:text-slate-500 text-[8px] font-black uppercase tracking-[0.15em]">Reserva</p>
              </div>
              <p className="text-lg md:text-xl font-black text-indigo-900 dark:text-emerald-400 heading-display">{maskValue(stats.totalEmergency)}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {items.length === 0 ? (
            <div className="col-span-full py-16 text-center bg-white dark:bg-slate-900 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl animate-in fade-in duration-1000">
               <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800/50 rounded-xl flex items-center justify-center mx-auto mb-4 text-slate-200 dark:text-slate-800 shadow-inner">
                 <i className="fa-solid fa-piggy-bank text-xl"></i>
               </div>
               <p className="text-[8px] font-black uppercase tracking-[0.15em] text-slate-400">Cofre Vazio • Comece a Poupar</p>
            </div>
          ) : (
            items.map((item, index) => {
              const progressRaw = item.targetAmount ? (item.amount / item.targetAmount) * 100 : 0;
              const progress = Math.min(100, progressRaw);
              const theme = {
                savings: { 
                  color: 'bg-blue-600', 
                  gradient: 'from-blue-600 to-indigo-600',
                  text: 'text-blue-600 dark:text-blue-400', 
                  bg: 'bg-blue-50 dark:bg-blue-900/20', 
                  icon: 'fa-piggy-bank' 
                },
                emergency: { 
                  color: 'bg-emerald-600', 
                  gradient: 'from-emerald-600 to-teal-600',
                  text: 'text-emerald-600 dark:text-emerald-400', 
                  bg: 'bg-emerald-50 dark:bg-emerald-900/20', 
                  icon: 'fa-shield-heart' 
                }
              }[item.type];
              
              return (
                <div key={item.id} className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/60 dark:border-slate-800 shadow-premium hover:shadow-xl hover:-translate-y-1 transition-all duration-500 flex flex-col group relative overflow-hidden animate-in fade-in slide-in-from-bottom-10" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-500/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  
                  <div className="flex justify-between items-start mb-4 relative z-10">
                    <div className={`w-9 h-9 ${theme.bg} ${theme.text} rounded-lg flex items-center justify-center text-base shadow-sm shrink-0 group-hover:scale-110 transition-all duration-500`}>
                      <i className={`fa-solid ${theme.icon}`}></i>
                    </div>
                    <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                      <button onClick={() => handleOpenModal(item)} className="w-7 h-7 flex items-center justify-center bg-white dark:bg-slate-800 text-slate-400 hover:text-blue-600 rounded-lg transition-all border border-slate-100 dark:border-slate-700 shadow-sm"><i className="fa-solid fa-pen text-[8px]"></i></button>
                      <button onClick={() => onDelete(item.id)} className="w-7 h-7 flex items-center justify-center bg-white dark:bg-slate-800 text-slate-400 hover:text-red-600 rounded-lg transition-all border border-slate-100 dark:border-slate-700 shadow-sm"><i className="fa-solid fa-trash text-[8px]"></i></button>
                    </div>
                  </div>
                  
                  <div className="mb-3 relative z-10">
                    <h4 className="font-black text-indigo-950 dark:text-slate-50 text-sm truncate leading-tight mb-0.5 uppercase tracking-tight heading-display">{item.name}</h4>
                    <span className={`text-[8px] font-black uppercase tracking-[0.15em] ${theme.text} opacity-80`}>
                      {item.type === 'savings' ? 'Poupança' : 'Reserva'}
                    </span>
                  </div>

                  <div className="space-y-3 relative z-10 mt-auto">
                    <div className="space-y-0.5">
                      <p className="text-xl font-black text-indigo-900 dark:text-slate-50 leading-none tracking-tighter heading-display">{maskValue(item.amount)}</p>
                      {!!item.targetAmount && (
                        <div className="flex justify-between items-center px-0.5">
                           <p className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.12em]">Meta: {maskValue(item.targetAmount)}</p>
                           <p className={`text-[9px] font-black ${theme.text} uppercase tracking-widest`}>{progress.toFixed(0)}%</p>
                        </div>
                      )}
                    </div>
                    
                    {!!item.targetAmount && (
                      <div className="w-full bg-slate-100 dark:bg-slate-800/50 rounded-full h-2 overflow-hidden border border-slate-100 dark:border-slate-800 p-0.5">
                        <div 
                          className={`h-full rounded-full transition-all duration-1000 bg-gradient-to-r ${theme.gradient} shadow-[0_0_10px_rgba(0,0,0,0.1)]`} 
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    )}

                    <button 
                      onClick={() => handleOpenDeposit(item)}
                      className={`w-full py-2.5 rounded-lg font-black text-[8px] uppercase tracking-[0.12em] transition-all ${theme.bg} ${theme.text} hover:opacity-90 hover:shadow-lg active:scale-95 shadow-sm flex items-center justify-center gap-2 border border-transparent hover:border-current/10 group/btn`}
                    >
                      <i className="fa-solid fa-plus-circle text-xs group-hover/btn:rotate-90 transition-transform"></i>
                      Realizar Aporte
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-t-[2.5rem] md:rounded-[2.5rem] p-6 md:p-8 shadow-2xl animate-in slide-in-from-bottom border-t border-slate-100 dark:border-slate-800 overflow-y-auto max-h-[90vh]">
            <h3 className="text-xl font-black text-indigo-950 dark:text-slate-50 mb-6 tracking-tight uppercase">Definir Objetivo</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                {(['savings', 'emergency'] as const).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex-1 py-3 rounded-lg text-[9px] font-black transition-all uppercase tracking-widest ${type === t ? 'bg-white dark:bg-slate-700 text-blue-600 shadow-sm' : 'text-slate-400'}`}
                  >
                    {t === 'savings' ? 'Poupança' : 'Reserva'}
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Título do Objetivo</label>
                <input required value={name} onChange={e => setName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-5 py-4 text-sm font-bold text-slate-800 dark:text-slate-100 outline-none focus:ring-4 focus:ring-blue-600/5 transition-all" placeholder="Ex: Viagem..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Saldo Atual</label>
                  <input required type="text" inputMode="decimal" value={amount} onChange={e => setAmount(maskCurrency(e.target.value))} className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-5 py-4 text-sm font-black text-slate-800 dark:text-slate-100 outline-none focus:ring-4 focus:ring-blue-600/5 transition-all" placeholder="0,00" />
                </div>
                <div className="space-y-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Meta Final</label>
                  <input type="text" inputMode="decimal" value={target} onChange={e => setTarget(maskCurrency(e.target.value))} className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-5 py-4 text-sm font-black text-slate-800 dark:text-slate-100 outline-none focus:ring-4 focus:ring-blue-600/5 transition-all" placeholder="0,00" />
                </div>
              </div>
              <div className="pt-4 flex gap-3">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl active:scale-95 transition-all">Salvar</button>
                <button type="button" onClick={() => setIsModalOpen(false)} className="w-14 bg-slate-100 dark:bg-slate-800 text-slate-400 rounded-2xl active:scale-95 transition-all flex items-center justify-center"><i className="fa-solid fa-times"></i></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isDepositModalOpen && selectedItemForDeposit && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-t-[2.5rem] md:rounded-[2.5rem] p-7 shadow-2xl animate-in slide-in-from-bottom border-t border-slate-100 dark:border-slate-800">
            <h3 className="text-xl font-black text-indigo-950 dark:text-slate-100 mb-1 tracking-tight leading-none uppercase">Fazer Aporte</h3>
            <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest mb-6">Aporte em: {selectedItemForDeposit.name}</p>
            <form onSubmit={handleDepositSubmit} className="space-y-6">
              <div className="relative">
                <span className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 font-black text-lg">R$</span>
                <input 
                  required 
                  autoFocus
                  type="text" 
                  inputMode="decimal"
                  value={depositAmount} 
                  onChange={e => setDepositAmount(maskCurrency(e.target.value))} 
                  className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-6 py-8 text-3xl font-black font-mono text-center text-slate-900 dark:text-slate-50 outline-none focus:ring-4 focus:ring-blue-500/5 transition-all" 
                />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="flex-1 bg-emerald-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-all">Confirmar</button>
                <button type="button" onClick={() => setIsDepositModalOpen(false)} className="w-14 bg-slate-100 dark:bg-slate-800 text-slate-400 rounded-2xl active:scale-95 transition-all flex items-center justify-center"><i className="fa-solid fa-times"></i></button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wealth;
